import { type PlanFromFileResult, planMigrationFromDevice } from "@bso/shared";

/**
 * ON-DEVICE path for legacy migration (Android/desktop inside Tauri).
 *
 * Serves REAL users: legacy app
 * (`com.biblestrongoffline`) **has no export**, and since new app
 * `applicationId` matches, v2.0 installs as UPDATE and private
 * directory survives. Native command `scan_legacy_v1` reads raw files
 * there and parsing uses the SAME shared kit as manual file import.
 * manual file import.
 *
 * Legacy data is never deleted or modified: safety net.
 */

/** Payload returned by native command `scan_legacy_v1`. */
interface LegacyScan {
  found: boolean;
  alreadyMigrated: boolean;
  highlightDbBase64: string | null;
  preferencesJson: string | null;
  sources: string[];
}

/** Are we running inside native container? */
function isNativeRuntime(): boolean {
  return typeof globalThis !== "undefined" && "__TAURI_INTERNALS__" in globalThis;
}

function decodeBase64(value: string): Uint8Array {
  // `atob` returns a binary string (one code unit per byte, 0..255).
  return Uint8Array.from(atob(value), (char) => char.codePointAt(0) ?? 0);
}

interface DeviceScanResult {
  available: boolean;
  alreadyMigrated: boolean;
  sources: string[];
  plan?: PlanFromFileResult;
}

/**
 * Scans device. Outside Tauri (web) returns `available: false` without
 * noise: manual file import is used there.
 */
export async function scanDeviceForLegacyData(): Promise<DeviceScanResult> {
  if (!isNativeRuntime()) {
    return { available: false, alreadyMigrated: false, sources: [] };
  }
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const scan = await invoke<LegacyScan>("scan_legacy_v1");
    if (!scan.found) {
      return { available: false, alreadyMigrated: scan.alreadyMigrated, sources: scan.sources };
    }
    return {
      available: true,
      alreadyMigrated: scan.alreadyMigrated,
      sources: scan.sources,
      plan: planMigrationFromDevice({
        highlightDb: scan.highlightDbBase64 ? decodeBase64(scan.highlightDbBase64) : undefined,
        preferencesJson: scan.preferencesJson ?? undefined,
      }),
    };
  } catch (error) {
    // Scan failure must NEVER break startup: manual migration
    // file import remains available.
    console.warn("Legacy device scan failed", error);
    return { available: false, alreadyMigrated: false, sources: [] };
  }
}

/**
 * Marks migration as resolved to avoid prompting again.
 * Does NOT delete anything from legacy: sentinel lives in new app directory.
 */
export async function markDeviceMigrated(): Promise<void> {
  if (!isNativeRuntime()) {
    return;
  }
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("mark_legacy_migrated_v1");
  } catch (error) {
    console.warn("Could not write the legacy migration sentinel", error);
  }
}
